import { useState } from "react";
import { motion } from "framer-motion";
import ABGInputForm from "./components/ABGInputForm";
import ABGResults from "./components/ABGResults";
import PHMeter from "./components/PHMeter";
import ParameterGauge from "./components/ParameterGauge";
import { analyzeABG } from "./lib/abgLogic";

function App() {
  const [abgValues, setAbgValues] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [showResults, setShowResults] = useState(false);

  const handleAnalyze = (values, acute) => {
    setAbgValues(values);
    const result = analyzeABG(values, acute);
    setAnalysis(result);
    setShowResults(true);

    // Scroll to results after a short delay
    setTimeout(() => {
      const resultsElement = document.getElementById("results-section");
      if (resultsElement) {
        resultsElement.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  const handleLoadExample = (values) => {
    setAbgValues(values);
    setShowResults(false);
  };

  return (
    <div className="min-h-screen bg-dark-gradient py-8 px-4 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-0 left-0 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20"
          animate={{
            x: [0, 100, 0],
            y: [0, 50, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20"
          animate={{
            x: [0, -100, 0],
            y: [0, 100, 0],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute bottom-0 left-1/2 w-96 h-96 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20"
          animate={{
            x: [-50, 50, -50],
            y: [0, -50, 0],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-4xl mx-auto relative z-10"
      >
        <header className="text-center mb-8">
          <motion.h1
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 mb-2"
          >
            Interactive ABG Analyzer
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="text-xl text-gray-300"
          >
            Boston Approach to Arterial Blood Gas Interpretation
          </motion.p>
        </header>

        <ABGInputForm
          onAnalyze={handleAnalyze}
          onLoadExample={handleLoadExample}
        />

        {abgValues && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-8 glass-card p-6 rounded-xl shadow-2xl"
          >
            <h2 className="text-2xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
              ABG Parameters
            </h2>

            <PHMeter pH={abgValues.pH} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ParameterGauge
                title="PaCO2"
                value={abgValues.paCO2}
                unit="mmHg"
                minValue={10}
                maxValue={80}
                normalMin={35}
                normalMax={45}
                colorLow="#3b82f6" // Blue for low (alkalosis)
                colorNormal="#22c55e" // Green for normal
                colorHigh="#ef4444" // Red for high (acidosis)
              />

              <ParameterGauge
                title="HCO3-"
                value={abgValues.hco3}
                unit="mEq/L"
                minValue={5}
                maxValue={45}
                normalMin={22}
                normalMax={26}
                colorLow="#ef4444" // Red for low (acidosis)
                colorNormal="#22c55e" // Green for normal
                colorHigh="#3b82f6" // Blue for high (alkalosis)
              />

              {abgValues.sodium && abgValues.chloride && (
                <ParameterGauge
                  title="Anion Gap"
                  value={
                    abgValues.sodium - (abgValues.chloride + abgValues.hco3)
                  }
                  unit="mEq/L"
                  minValue={0}
                  maxValue={30}
                  normalMin={8}
                  normalMax={12}
                  colorLow="#3b82f6" // Blue for low
                  colorNormal="#22c55e" // Green for normal
                  colorHigh="#ef4444" // Red for high
                />
              )}

              {abgValues.pO2 && (
                <ParameterGauge
                  title="PaO2"
                  value={abgValues.pO2}
                  unit="mmHg"
                  minValue={40}
                  maxValue={120}
                  normalMin={80}
                  normalMax={100}
                  colorLow="#ef4444" // Red for low
                  colorNormal="#22c55e" // Green for normal
                  colorHigh="#3b82f6" // Blue for high
                />
              )}

              {abgValues.sO2 && (
                <ParameterGauge
                  title="SaO2"
                  value={abgValues.sO2}
                  unit="%"
                  minValue={70}
                  maxValue={100}
                  normalMin={95}
                  normalMax={100}
                  colorLow="#ef4444" // Red for low
                  colorNormal="#22c55e" // Green for normal
                  colorHigh="#22c55e" // Green for high (can't be too high)
                />
              )}
            </div>
          </motion.div>
        )}

        {showResults && analysis && (
          <div id="results-section">
            <ABGResults
              steps={analysis.steps}
              finalDiagnosis={analysis.finalDiagnosis}
            />
          </div>
        )}

        <footer className="mt-12 text-center text-gray-400 text-sm">
          <p className="text-gray-300">
            © {new Date().getFullYear()} Interactive ABG Analyzer
            ResuscitateIndia
          </p>
          <p className="mt-1 text-gray-500">
            This tool is for educational purposes only, not for patient
            management. Always consult with a healthcare professional for
            medical advice. Thanks to my wife Surbhi who is a lawyer and forced
            me to enter this line in the code!.
          </p>
        </footer>
      </motion.div>
    </div>
  );
}

export default App;
